import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Mail, ArrowLeft } from 'lucide-react';

/**
 * IdentitySupportFallback — rendered on the AuthPage forgot-password
 * fallback step ("I can't provide both"). Dynamically resolves the
 * correct contact path based on the entered email's role:
 *
 *   • affiliate / lead → "Email State Supervisor"
 *     mailto: <state>+<supervisor_email_routing_token>@<domain>
 *     funnels DIRECTLY into that supervisor's in-app inbox, not the
 *     unassigned queue.
 *
 *   • supervisor / admin / special user → "Contact Leadership"
 *     non-clickable (these roles ARE leadership; out-of-band help).
 *
 *   • unknown email → "Email Support"
 *     mailto: upliftsupport@collectiveorb.com
 *
 * Resolver: GET /api/auth/identity-support-contact?email=<email>
 */
export default function IdentitySupportFallback({ email, onBack, api }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!email) { setLoading(false); return; }
    setLoading(true);
    api.get('/auth/identity-support-contact', { params: { email } })
      .then((r) => { if (!cancelled) setInfo(r.data || null); })
      .catch(() => { if (!cancelled) setInfo({ kind: 'support', label: 'Email Support', mailto: 'mailto:upliftsupport@collectiveorb.com', to: 'upliftsupport@collectiveorb.com' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [email]);

  const subject = encodeURIComponent('Identity verification help — Uplift account access');
  const baseBody = `Hi,\n\nI'm unable to access my Uplift account (${email}). I cannot provide my authenticator code or recovery key. Please help me verify my identity so I can recover access.\n\nThanks.`;
  // Append the ORB-ID marker (hidden-ish — visible as small footer)
  // so the supervisor's inbox can correlate this email back to the
  // override request flow. The marker is parsed server-side from the
  // inbound subject/body — survives Outlook/iOS Mail reply quoting.
  const marker = info?.marker || '';
  const bodyText = encodeURIComponent(
    marker
      ? `${baseBody}\n\n---\nReference: ${marker}\n(Please keep this reference number as it helps our team process your request.)`
      : baseBody
  );

  const buildHref = () => {
    if (!info?.mailto || !info?.to) return null;
    const sep = info.mailto.includes('?') ? '&' : '?';
    return `${info.mailto}${sep}subject=${subject}&body=${bodyText}`;
  };

  const clickable = info?.mailto && info?.to;

  return (
    <div className="space-y-4 text-center">
      <div className="p-4 bg-red-50 rounded-lg mb-4">
        <Mail className="w-10 h-10 mx-auto mb-2 text-red-600" />
        <p className="text-sm text-red-700 font-medium">
          Unable to verify your identity
        </p>
      </div>

      {loading ? (
        <div className="py-3 text-sm text-gray-500" data-testid="identity-support-loading">
          Resolving best contact…
        </div>
      ) : (
        <>
          <p className="text-gray-600" data-testid="identity-support-cta">
            Please contact our team for assistance.
          </p>
          {clickable ? (
            <a
              href={buildHref()}
              className="block w-full"
              data-testid="identity-support-mailto-link"
            >
              <Button
                type="button"
                className="w-full rounded-full bg-[#586D52] hover:bg-[#4a5d46] text-white"
                data-testid="identity-support-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                {info?.label || 'Email Support'}
              </Button>
            </a>
          ) : (
            <Button
              type="button"
              onClick={(e) => e.preventDefault()}
              className="w-full rounded-full bg-[#586D52] hover:bg-[#586D52] text-white cursor-default"
              data-testid="identity-support-button-disabled"
            >
              {info?.label || 'Contact Leadership'}
            </Button>
          )}
        </>
      )}

      <Button
        type="button"
        variant="ghost"
        className="w-full text-gray-500 hover:text-gray-700"
        onClick={onBack}
        data-testid="identity-support-back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Sign In
      </Button>
    </div>
  );
}
